import type { ToolCallMessagePartStatus } from '@assistant-ui/react';

export type ToolOutcome =
  | 'running'
  | 'complete'
  | 'error'
  | 'cancelled'
  | 'approval'
  | 'incomplete';
type ToolEvidence = {
  toolName?: string;
  status?: ToolCallMessagePartStatus;
  isError?: boolean;
  result?: unknown;
  approval?: unknown;
  interrupt?: unknown;
};

/**
 * A normal Chat `save_artifact` whose writer answered without a committed receipt: `unknown` or
 * `unresolved` (we cannot say whether it was saved) or `conflict` (someone saved first). It is not
 * Complete; `unconfirmed` is the one that owes the person the "couldn't confirm" line.
 */
export function saveArtifactState(
  toolName: string | undefined,
  result: unknown,
): 'unconfirmed' | 'conflict' | null {
  if (toolName !== 'save_artifact' || result === null || typeof result !== 'object') return null;
  const status = (result as { status?: unknown }).status;
  if (status === 'unknown' || status === 'unresolved') return 'unconfirmed';
  return status === 'conflict' ? 'conflict' : null;
}

/** Presentation only: never changes execution, approval, or result evidence. */
export function toolOutcome({
  toolName,
  status,
  isError,
  result,
  approval,
  interrupt,
}: ToolEvidence): ToolOutcome {
  if (status?.type === 'incomplete' && status.reason === 'cancelled') return 'cancelled';
  if (
    status?.type === 'incomplete' ||
    isError ||
    (result !== null &&
      typeof result === 'object' &&
      'status' in result &&
      result.status === 'error')
  )
    return 'error';
  // The SDK also uses requires-action for a stopped server call without a result.
  // Only an explicit gate authorizes us to offer approval controls.
  if (status?.type === 'requires-action')
    return approval != null || interrupt != null ? 'approval' : 'incomplete';
  if (status?.type === 'running') return 'running';
  // A save the writer did not confirm is not Complete, whatever the call's own status says.
  if (saveArtifactState(toolName, result)) return 'incomplete';
  return 'complete';
}

/** Readable identifiers, not an inference about what a tool accomplished. */
export function toolLabel(identifier: string) {
  const separator = identifier.indexOf('__');
  const pack = separator < 0 ? '' : identifier.slice(0, separator);
  const name = separator < 0 ? identifier : identifier.slice(separator + 2);
  const words = name.replace(/[_-]+/g, ' ').trim();
  return { pack, label: words ? words[0].toUpperCase() + words.slice(1) : identifier };
}

export function toolInputSummary(argsText?: string): string {
  if (!argsText) return '';
  try {
    const args: unknown = JSON.parse(argsText);
    if (!args || typeof args !== 'object' || Array.isArray(args)) return '';
    // Only scalar fields: nested payloads and unfinished JSON belong in details.
    return Object.entries(args)
      .filter(
        ([, value]) =>
          typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean',
      )
      .slice(0, 3)
      .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${String(value)}`)
      .join(' · ')
      .slice(0, 180);
  } catch {
    return '';
  }
}

export function activityOutcome(parts: ToolEvidence[]): ToolOutcome {
  const outcomes = parts.map(toolOutcome);
  return (
    (['approval', 'running', 'error', 'incomplete', 'cancelled'] as const).find((kind) =>
      outcomes.includes(kind),
    ) ?? 'complete'
  );
}
